import { useState, useCallback, useMemo } from 'react';
import Popover from '../Popover';
import BookmarksTooltip from './BookmarksTooltip';
import { FavoriteIcon, FavoriteIconSolid } from '../../nav-ui-components/Icons/FavoritesIcon';
import styled, { css } from 'foundations-theming-specialty/utils';
import { useTranslation } from '../../hooks/useNavConfig';
import { LinkBadgeType } from '../VerticalNav/hooks/useLinkBadgeData';
import { MAX_BOOKMARK_ITEMS } from '../../constants/Bookmarks';
import { MenuItemLevel } from '../../constants';
import TrackedButton from '../TrackedButton';
import { useHideTooltipOnTriggerJustClicked } from './hooks/useHideTooltipOnTriggerJustClicked';
import useBookmarksCount from './hooks/useBookmarksCount';
import { useTheme } from 'foundations-theming-specialty/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledTrackedButton = styled(TrackedButton)(({
  $shouldDisplay,
  $displayLinkBadge,
  theme
}) => {
  return {
    padding: $displayLinkBadge ? '0px 4px' : '4px 0 4px 4px',
    opacity: `${$shouldDisplay ? '1' : '0'} !important`,
    background: theme.color['fill-transparent-default'],
    marginBottom: `${theme.space['0']} !important`,
    border: 0
  };
});
const favoriteIconStyles = ({
  isHoveringOverNavItem,
  isBookmarksMenu,
  isHoveringOverBookmarks,
  theme
}) => {
  const fillColor = isHoveringOverNavItem && !isBookmarksMenu || isHoveringOverBookmarks && isBookmarksMenu ? theme.color['global-shell-icon-bookmark-hover'] : theme.color['global-shell-icon-bookmark-selected'];
  return css(["fill:", ";display:flex;width:14px !important;"], fillColor);
};
export const Bookmark = ({
  isBookmarked,
  onBookmarkClicked,
  rightSideBadgeType,
  bookmarkId,
  bookmarkPrimaryNavCategory,
  bookmarkLabel,
  isHoveringOverBookmarks,
  setIsHoveringOverBookmarks,
  displayLinkBadge,
  isBookmarksMenu,
  isHoveringOverNavItem
}) => {
  const bookmarksCount = useBookmarksCount();
  const theme = useTheme();
  const [isJustRemovedFromFavorites, setIsJustRemovedFromFavorites] = useState(false);
  const {
    shouldShowTooltip,
    setShouldShowTooltip
  } = useHideTooltipOnTriggerJustClicked(isBookmarked);
  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    const isBookmarksLimitReached = bookmarksCount >= MAX_BOOKMARK_ITEMS;
    if (isBookmarksLimitReached && !isBookmarked) {
      return;
    }
    if (isBookmarked) {
      // Being toggled to false. So, being removed from favs. To not show the solid icon when removed
      // from favorites but still hovering
      setIsJustRemovedFromFavorites(true);
    }
    onBookmarkClicked(!isBookmarked);
  };
  const {
    t: translate
  } = useTranslation();
  const getTooltipPadding = () => {
    if (rightSideBadgeType) {
      return rightSideBadgeType === LinkBadgeType.upgradeArrow ? 58 : 85;
    }
    return 40;
  };
  const getTooltipContent = () => {
    const isBookmarksLimitReached = bookmarksCount >= MAX_BOOKMARK_ITEMS;
    if (isBookmarksLimitReached && !isBookmarked) {
      return translate('nav.bookmarks3.limitReached');
    }
    return isBookmarked ? translate('nav.bookmarks2.remove') : translate('nav.bookmarks2.add');
  };
  const getAriaLabel = () => {
    const isBookmarksLimitReached = bookmarksCount >= MAX_BOOKMARK_ITEMS;
    if (isBookmarksLimitReached && !isBookmarked) {
      return translate('nav.bookmarks3.limitReached');
    }
    return isBookmarked ? translate('nav.bookmarks.button.remove.ariaLabel', {
      menuItem: bookmarkLabel
    }) : translate('nav.bookmarks.button.add.ariaLabel', {
      menuItem: bookmarkLabel
    });
  };
  const getEventData = () => {
    if (isBookmarked) {
      // Going to be toggled to false... so, going to be removed
      return {
        action: 'Removed from shortcuts',
        shortcutCount: bookmarksCount - 1
      };
    }
    if (bookmarksCount >= MAX_BOOKMARK_ITEMS) {
      return {
        action: 'Shortcuts limit reached',
        shortcutCount: MAX_BOOKMARK_ITEMS
      };
    }
    return {
      action: 'Added to shortcuts',
      shortcutCount: bookmarksCount + 1
    };
  };
  const onFocus = useCallback(() => {
    setIsHoveringOverBookmarks(true);
    setShouldShowTooltip(true);
    // Reset the state for correctly showing the solid star
    setIsJustRemovedFromFavorites(false);
  }, [setIsHoveringOverBookmarks, setShouldShowTooltip]);
  const onBlur = useCallback(() => {
    setIsHoveringOverBookmarks(false);
    setShouldShowTooltip(false);
    // Reset the state for correctly showing the solid star
    setIsJustRemovedFromFavorites(false);
  }, [setIsHoveringOverBookmarks, setShouldShowTooltip]);
  const isSolid = isBookmarked || isHoveringOverBookmarks && !isJustRemovedFromFavorites;
  const shouldDisplay = isHoveringOverNavItem || isBookmarked && !isBookmarksMenu;
  const svgStyles = useMemo(() => {
    return favoriteIconStyles({
      isHoveringOverNavItem,
      isBookmarksMenu,
      isHoveringOverBookmarks,
      theme
    });
  }, [isHoveringOverNavItem, isBookmarksMenu, isHoveringOverBookmarks, theme]);
  return /*#__PURE__*/_jsx(Popover, {
    isOpen: shouldShowTooltip && isHoveringOverBookmarks,
    placement: 'right',
    content: /*#__PURE__*/_jsx(BookmarksTooltip, {
      text: getTooltipContent()
    }),
    padding: getTooltipPadding(),
    children: /*#__PURE__*/_jsx(StyledTrackedButton, {
      onFocus: onFocus,
      onBlur: onBlur,
      onMouseEnter: onFocus,
      onMouseLeave: onBlur,
      onClick: handleClick,
      id: `${bookmarkId}-shortcut`,
      "data-menu-item-level": MenuItemLevel.SECONDARY,
      "data-menu-item-group": bookmarkPrimaryNavCategory,
      "data-click-action": getEventData().action,
      "data-shortcut-count": getEventData().shortcutCount,
      "aria-label": getAriaLabel(),
      "data-test-id": "bookmark-icon",
      $displayLinkBadge: displayLinkBadge,
      $shouldDisplay: shouldDisplay,
      children: isSolid ? /*#__PURE__*/_jsx(FavoriteIconSolid, {
        svgStyles: svgStyles
      }) : /*#__PURE__*/_jsx(FavoriteIcon, {
        svgStyles: svgStyles
      })
    })
  });
};